import { Box, Skeleton, Stack, Typography } from '@mui/material';
import {
  Inbox as EmptyIcon,
  PendingActions as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
} from '@mui/icons-material';
import type { GroupCreationRequest } from '@penticton-dev-events/shared';

import GroupCreationRequestCard from './GroupCreationRequestCard';

export interface GroupCreationRequestListProps {
  /**
   * Array of group creation requests to display
   */
  readonly requests: readonly GroupCreationRequest[];

  /**
   * Display variant for all cards in the list
   * - 'user-compact': Minimal view for user's request list
   * - 'user-detailed': Full details for user viewing their requests
   * - 'admin': Full details with admin actions
   */
  readonly variant: 'user-compact' | 'user-detailed' | 'admin';

  /**
   * Loading state - shows skeleton loaders when true
   */
  readonly loading?: boolean;

  /**
   * Callback when approve button is clicked (admin only)
   */
  readonly onApprove?: (requestId: string) => void;

  /**
   * Callback when reject button is clicked (admin only)
   */
  readonly onReject?: (requestId: string) => void;

  /**
   * Callback when cancel button is clicked (user only)
   */
  readonly onCancel?: (requestId: string) => void;

  /**
   * Callback when view/details button is clicked
   */
  readonly onView?: (requestId: string) => void;

  /**
   * Callback when group link is clicked (approved requests only)
   */
  readonly onGroupLink?: (groupId: string) => void;

  /**
   * Optional filter status to show in empty state message
   */
  readonly filterStatus?: 'pending' | 'approved' | 'rejected';
}

/**
 * List wrapper component for group creation requests
 *
 * Features:
 * - Responsive grid layout (1 col mobile, 2 cols tablet, 3 cols desktop)
 * - Loading skeletons matching card dimensions
 * - Context-aware empty states with helpful messages
 * - Accessibility (semantic HTML, ARIA labels)
 * - Consistent spacing and alignment
 *
 * Layout:
 * - xs: 1 column (mobile)
 * - sm: 2 columns (tablet)
 * - md: 2 columns (desktop)
 * - lg: 3 columns (wide desktop)
 */
const GroupCreationRequestList = ({
  requests,
  variant,
  loading = false,
  onApprove,
  onReject,
  onCancel,
  onView,
  onGroupLink,
  filterStatus,
}: GroupCreationRequestListProps) => {
  // Loading State
  if (loading) {
    return (
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={3}
        justifyContent="flex-start"
        role="status"
        aria-label="Loading group creation requests"
      >
        {Array.from({ length: variant === 'user-compact' ? 3 : 6 }).map((_, index) => (
          <Box
            key={index}
            sx={{
              flexBasis: {
                xs: '100%',
                sm: 'calc(50% - 12px)',
                lg:
                  variant === 'user-compact'
                    ? 'calc(50% - 12px)'
                    : 'calc(33.333% - 16px)',
              },
              minHeight: variant === 'user-compact' ? 120 : 280,
            }}
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height={variant === 'user-compact' ? 120 : 280}
              sx={{ borderRadius: 2 }}
              animation="wave"
            />
          </Box>
        ))}
      </Stack>
    );
  }

  // Empty State
  if (requests.length === 0) {
    const emptyStateConfig = {
      pending: {
        icon: (
          <PendingIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
        ),
        title: 'No pending requests',
        description: 'All group creation requests have been reviewed.',
      },
      approved: {
        icon: (
          <ApprovedIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
        ),
        title: 'No approved requests',
        description: 'No group creation requests have been approved yet.',
      },
      rejected: {
        icon: (
          <RejectedIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
        ),
        title: 'No rejected requests',
        description: 'No group creation requests have been rejected.',
      },
      default: {
        icon: <EmptyIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />,
        title: variant === 'admin' ? 'No group creation requests' : 'No requests yet',
        description:
          variant === 'admin'
            ? 'Group creation requests will appear here when users submit them.'
            : 'You have not submitted any group creation requests. Submit a request to create a new community group.',
      },
    };

    const config = filterStatus
      ? emptyStateConfig[filterStatus]
      : emptyStateConfig.default;

    return (
      <Stack
        alignItems="center"
        paddingY={8}
        spacing={2}
        role="status"
        aria-label={config.title}
      >
        {config.icon}
        <Typography variant="h6" color="text.primary">
          {config.title}
        </Typography>
        <Typography
          color="text.secondary"
          maxWidth={480}
          textAlign="center"
          variant="body2"
        >
          {config.description}
        </Typography>
      </Stack>
    );
  }

  // Requests List
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={3}
      justifyContent="flex-start"
      component="section"
      aria-label={`Group creation requests (${requests.length} ${requests.length === 1 ? 'request' : 'requests'})`}
    >
      {requests.map(request => (
        <Box
          key={request.id}
          sx={{
            flexBasis: {
              xs: '100%',
              sm: 'calc(50% - 12px)',
              lg:
                variant === 'user-compact' ? 'calc(50% - 12px)' : 'calc(33.333% - 16px)',
            },
            display: 'flex',
          }}
        >
          <GroupCreationRequestCard
            request={request}
            variant={variant}
            onApprove={onApprove}
            onReject={onReject}
            onCancel={onCancel}
            onView={onView}
            onGroupLink={onGroupLink}
          />
        </Box>
      ))}
    </Stack>
  );
};

export default GroupCreationRequestList;
